import { baseFetch } from "./apiClient";
import { withUploadMimeType } from "./eventUploadFormats";

/** Upload event media directly to S3, then finalize ownership and validation. */
export const uploadEventFile = async (
  candidate,
  fieldName = "resourceFile",
  { signal } = {},
) => {
  const file = withUploadMimeType(candidate);
  if (!file?.type) throw new Error("The file type could not be determined.");
  const { data: presign } = await baseFetch({
    url: "/v1/web/upload/presign",
    method: "POST",
    signal,
    body: {
      fieldName,
      mimetype: file.type,
      size: file.size,
      originalName: file.name,
    },
  });

  try {
    const completion = { key: presign.key, uploadToken: presign.uploadToken };
    if (presign.mode === "multipart") {
      completion.uploadId = presign.uploadId;
      completion.parts = [];
      // Upload sequentially so large videos do not send every chunk at once.
      for (const { partNumber, url } of presign.partUrls) {
        const start = (partNumber - 1) * presign.partSize;
        const response = await fetch(url, {
          method: "PUT",
          signal,
          body: file.slice(start, start + presign.partSize),
        });
        if (!response.ok) throw new Error("Event file upload failed.");
        const ETag = response.headers.get("ETag");
        if (!ETag) throw new Error("The upload response is missing its ETag.");
        completion.parts.push({ PartNumber: partNumber, ETag });
      }
    } else if (presign.mode === "single") {
      const form = new FormData();
      for (const [key, value] of Object.entries(presign.fields)) {
        form.append(key, value);
      }
      // S3 requires the file after every signed form field.
      form.append("file", file);
      const response = await fetch(presign.url, {
        method: "POST",
        body: form,
        signal,
      });
      if (!response.ok) throw new Error("Event file upload failed.");
    } else {
      throw new Error("Unsupported event upload mode.");
    }
    const { data: completed } = await baseFetch({
      url: "/v1/web/upload/presign/complete",
      method: "POST",
      signal,
      body: completion,
    });
    return completed;
  } catch (error) {
    if (presign.mode === "multipart") {
      try {
        await baseFetch({
          url: "/v1/web/upload/presign/abort",
          method: "POST",
          body: {
            key: presign.key,
            uploadToken: presign.uploadToken,
            uploadId: presign.uploadId,
          },
        });
      } catch (abortError) {
        console.error("Could not abort the event upload", abortError);
      }
    }
    throw error;
  }
};

/** Replace local files with the metadata accepted by JSON event requests. */
export const prepareEventMedia = async (
  fields,
  uploads = new WeakMap(),
  { signal } = {},
) => {
  const upload = async (file, fieldName) => {
    const cached = uploads.get(file);
    if (cached?.fieldName === fieldName) return cached.data;
    const data = await uploadEventFile(file, fieldName, { signal });
    // Retain completed uploads when the event save fails and the sponsor retries.
    uploads.set(file, { fieldName, data });
    return data;
  };
  const body = { ...fields };
  if (body.resourceFile instanceof File) {
    const file = withUploadMimeType(body.resourceFile);
    const uploaded = await upload(body.resourceFile, "resourceFile");
    body.resourceFile = {
      url: uploaded.url,
      // Legacy metadata takes the filename from an encoded upload URL.
      fileName: encodeURIComponent(file.name),
      fileSize: uploaded.size,
      fileType:
        file.type === "application/pdf"
          ? ".pdf"
          : uploaded.extension.toLowerCase(),
    };
  }
  if (body.photo instanceof Blob) {
    const uploaded = await upload(body.photo, "eventPhoto");
    body.eventPhoto = uploaded.url;
  }
  delete body.photo;
  return body;
};
